import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable to handle iOS virtual keyboard viewport issues.
 * Updates CSS custom properties with the visual viewport height and offset,
 * allowing the layout to shrink and reposition when the keyboard opens.
 *
 * CSS Variables set:
 * - --visual-viewport-height: The height of the visual viewport
 * - --visual-viewport-offset-top: The scroll offset (iOS scrolls page when keyboard opens)
 */
export function useVisualViewport() {
  const viewportHeight = ref(window.innerHeight);
  const viewportOffsetTop = ref(0);

  function updateViewport() {
    if (window.visualViewport) {
      viewportHeight.value = window.visualViewport.height;
      viewportOffsetTop.value = window.visualViewport.offsetTop;

      document.documentElement.style.setProperty(
        '--visual-viewport-height',
        `${window.visualViewport.height}px`
      );
      document.documentElement.style.setProperty(
        '--visual-viewport-offset-top',
        `${window.visualViewport.offsetTop}px`
      );
    } else {
      viewportHeight.value = window.innerHeight;
      viewportOffsetTop.value = 0;

      document.documentElement.style.setProperty(
        '--visual-viewport-height',
        `${window.innerHeight}px`
      );
      document.documentElement.style.setProperty(
        '--visual-viewport-offset-top',
        '0px'
      );
    }
  }

  onMounted(() => {
    updateViewport();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
    } else {
      window.addEventListener('resize', updateViewport);
    }
  });

  onUnmounted(() => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', updateViewport);
      window.visualViewport.removeEventListener('scroll', updateViewport);
    } else {
      window.removeEventListener('resize', updateViewport);
    }
  });

  return {
    viewportHeight,
    viewportOffsetTop
  };
}
